import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LogOut, Menu, X, LayoutDashboard, Newspaper, Users,
  MessageSquare, Calendar, Route, Image as ImageIcon, Map as MapIcon,
  Settings, Palette, Check, RotateCcw, Download, Monitor
} from "lucide-react";
import logo from "../../assets/logo.png";
import NotificationBell from "./NotificationBell";
import { useHubTheme } from "@/context/HubThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const NAV_ITEMS = [
  { path: "/drivershub/home", label: "Dashboard", icon: LayoutDashboard },
  { path: "/drivershub/events", label: "Events", icon: Calendar },
  { path: "/drivershub/news", label: "News", icon: Newspaper },
  { path: "/drivershub/chat", label: "Chat", icon: MessageSquare },
  { path: "/drivershub/karte", label: "Karte", icon: MapIcon },
  { path: "/drivershub/galerie", label: "Galerie", icon: ImageIcon },
  { path: "/drivershub/statistiken", label: "Statistiken", icon: Route },
  { path: "/drivershub/team", label: "Team", icon: Users },
];

const HR_ROLES = ["hr team", "hr-team", "hr", "personal team", "personal-team", "personalteam", "personal", "personalabteilung"];

import { BACKEND_URL } from '@/config';
const SETUP_URL = `${BACKEND_URL}/api/updates/download/setup.exe`;

function NavLinkItem({ item, pathname, iconOnly, onPrefetch, onClick, mobile = false }) {
  const isActive = pathname.startsWith(item.path);
  const Icon = item.icon;
  const iconsOnly = iconOnly && !mobile;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      onMouseEnter={() => onPrefetch(item.path)}
      title={item.label}
      className={`relative flex items-center gap-2 font-bold uppercase tracking-[0.2em] text-[11px] transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${mobile
        ? `w-full px-3.5 py-3 rounded-xl border ${isActive ? 'bg-amber-400/10 text-amber-400 border-amber-400/25' : 'text-zinc-400 border-transparent hover:bg-white/[0.04] hover:text-white'}`
        : `${iconsOnly ? "px-2.5" : "px-3"} py-2 ${isActive ? 'text-white' : 'text-slate-400 hover:text-white'}`
        }`}
    >
      <Icon size={16} className="shrink-0" />
      {!iconsOnly && <span>{item.label}</span>}
      {isActive && !mobile && (
        <motion.span
          layoutId="mgmtNavActiveIndicator"
          className={`absolute ${iconsOnly ? "inset-x-2" : "inset-x-3"} -bottom-0.5 h-0.5 rounded-full bg-amber-400`}
          transition={{ type: "spring", stiffness: 400, damping: 32 }}
        />
      )}
    </Link>
  );
}

export default function ManagementNav() {
  const { user, logout, isAdmin, hasRole } = useAuth();
  const { accentColor, setAccentColor, resetAccentColor, presets, defaultColor } = useHubTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [customHex, setCustomHex] = useState(accentColor || defaultColor);

  useEffect(() => {
    setCustomHex(accentColor || defaultColor);
  }, [accentColor, defaultColor]);

  const canSeeAdmin = isAdmin || hasRole(HR_ROLES);
  const items = [
    ...NAV_ITEMS,
    ...(canSeeAdmin ? [{ path: "/drivershub/admin", label: "Admin", icon: Settings }] : []),
  ];

  // Collapse labels to icons when there isn't enough horizontal space,
  // mirroring the app's navbar behaviour (icon mode 800–1400px, text >= 1400px).

  // Adaptive nav: text+icon on wide viewports, icon-only when space is tight.
  // (No horizontal scrolling — a scroll container would clip the active link's
  // underline, which extends below the link box.)
  const [iconOnly, setIconOnly] = useState(() => window.innerWidth < 1400);

  useEffect(() => {
    const handleResize = () => setIconOnly(window.innerWidth < 1400);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getAvatarUrl = (url) => api.imageUrl(url);

  const handlePrefetchRoute = (path) => {
    try {
      switch (path) {
        case "/drivershub/home":
          import("../pages/MgmtHomePage");
          api.prefetch("/events");
          api.prefetch("/news");
          break;
        case "/drivershub/events":
          import("../pages/MgmtEventsPage");
          api.prefetch("/events");
          break;
        case "/drivershub/news":
          import("../pages/MgmtNewsPage");
          api.prefetch("/news");
          break;
        case "/drivershub/chat":
          import("../pages/MgmtChatPage");
          api.prefetch("/chat/channels");
          break;
        case "/drivershub/karte":
          import("../pages/MgmtMapPage");
          api.prefetch("/live-map");
          break;
        case "/drivershub/galerie":
          import("../pages/MgmtGalleryPage");
          api.prefetch("/gallery");
          break;
        case "/drivershub/statistiken":
          import("../pages/MgmtStatsPage");
          api.prefetch("/stats/global");
          break;
        case "/drivershub/team":
          import("../pages/MgmtTeamPage");
          api.prefetch("/members");
          break;
        case "/drivershub/profil":
          import("../pages/MgmtProfilePage");
          if (user) {
            const uId = user.id || user.uid || user.user_id || user.username;
            if (uId) {
              api.prefetch(`/members/${encodeURIComponent(uId)}`);
              api.prefetch(`/members/${encodeURIComponent(uId)}/jobs`);
              api.prefetch(`/stats/member/${encodeURIComponent(uId)}`);
            }
          }
          break;
        case "/drivershub/admin":
          import("../pages/MgmtAdminPage");
          break;
        default:
          break;
      }
    } catch (_) {}
  };

  return (
    <>
      {/* ============ TOP BAR ============ */}
      <nav
        className="fixed top-0 left-0 right-0 z-[100] h-16 lg:h-20 flex items-center justify-between px-4 lg:px-6 glass-nav border-b border-zinc-900"
        data-testid="mgmt-nav"
      >
        <div className="absolute inset-0 -z-10 bg-black/60" />

        {/* Left: hamburger (mobile) + brand + desktop links */}
        <div className="relative flex items-center gap-4 lg:gap-6 min-w-0">
            <button
              onClick={() => setMenuOpen(true)}
              className="min-[800px]:hidden p-2 text-slate-400 hover:text-white bg-white/[0.03] rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              aria-label="Menü öffnen"
            >
            <Menu size={20} />
          </button>

          <Link to="/drivershub/home" className="flex items-center gap-3 shrink-0">
            <img src={logo} alt="Open Pipe Club" className="w-9 h-9 lg:w-10 lg:h-10 object-contain" />
            <span className="font-bold text-sm tracking-tight text-white hidden sm:block">Drivers Hub</span>
          </Link>

          <div className="hidden min-[800px]:flex items-center gap-1 border-l border-zinc-900 pl-4">
            {items.map((item) => (
              <NavLinkItem
                key={item.path}
                item={item}
                pathname={location.pathname}
                iconOnly={iconOnly}
                onPrefetch={handlePrefetchRoute}
              />
            ))}
          </div>
        </div>

        {/* Right: download + notifications + theme + profile + logout */}
        <div className="relative flex items-center gap-2 lg:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setDownloadModalOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-[10px] font-bold uppercase tracking-[0.2em] transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            title="OPC GameBridge Tracker herunterladen"
          >
            <Download size={14} />
            <span className="hidden sm:inline">Download</span>
          </button>

          <NotificationBell />

          <button
            type="button"
            onClick={() => setThemeModalOpen(true)}
            className="hidden min-[800px]:flex p-2 text-slate-400 hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors relative cursor-pointer items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            title="Farbe des DriversHubs anpassen"
            aria-label="Farbe des DriversHubs anpassen"
          >
            <Palette size={18} />
            <span
              className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-black shadow-sm"
              style={{ backgroundColor: accentColor }}
            />
          </button>

          {user && (
            <Link
              to="/drivershub/profil"
              onMouseEnter={() => handlePrefetchRoute("/drivershub/profil")}
              className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-white/5 transition-all group shrink-0"
              title={user.username}
            >
              <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10 shrink-0">
                {getAvatarUrl(user.avatar_url) ? (
                  <img src={getAvatarUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-primary/20 flex items-center justify-center text-[11px] text-primary font-bold">
                    {user.username?.charAt(0)}
                  </div>
                )}
              </div>
              <span className="hidden lg:inline text-xs font-bold text-slate-300 group-hover:text-white transition-colors max-w-[100px] truncate">{user.username}</span>
            </Link>
          )}

          <button
            onClick={handleLogout}
            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            title="Abmelden"
          >
            <LogOut size={18} />
          </button>
        </div>
      </nav>

      {/* ============ MOBILE DRAWER ============ */}
      <AnimatePresence>
        {menuOpen && (
          <div className="fixed inset-0 z-[800] min-[800px]:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-md"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute top-0 left-0 bottom-0 w-[300px] glass-nav border-r border-zinc-900 p-6 flex flex-col gap-2"
            >
              <div className="absolute inset-0 -z-10 bg-zinc-950/90" />
              <div className="relative flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="Open Pipe Club" className="w-8 h-8 object-contain" />
                  <span className="font-bold text-xs tracking-tight uppercase text-white">Drivers Hub</span>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                  aria-label="Menü schließen"
                >
                  <X size={22} />
                </button>
              </div>

              <div className="relative flex-1 flex flex-col gap-1.5 overflow-y-auto no-scrollbar">
                {items.map((item) => (
                  <NavLinkItem
                    key={item.path}
                    item={item}
                    pathname={location.pathname}
                    iconOnly={iconOnly}
                    onPrefetch={handlePrefetchRoute}
                    mobile={true}
                    onClick={() => setMenuOpen(false)}
                  />
                ))}
              </div>

              <div className="relative flex flex-col gap-2 pt-4 border-t border-white/5">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setThemeModalOpen(true); }}
                  className="flex items-center gap-3 w-full p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-white/5 transition-all text-left"
                >
                  <Palette size={18} className="text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold block">DriversHub Farbe</span>
                    <span className="text-[10px] text-slate-500 font-mono block uppercase">{accentColor}</span>
                  </div>
                  <div className="w-4 h-4 rounded-full border border-white/20 shadow-sm shrink-0" style={{ backgroundColor: accentColor }} />
                </button>

                {user && (
                  <Link
                    to="/drivershub/profil"
                    onClick={() => setMenuOpen(false)}
                    onMouseEnter={() => handlePrefetchRoute("/drivershub/profil")}
                    className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all group"
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center overflow-hidden border border-white/10 shrink-0">
                      {getAvatarUrl(user.avatar_url) ? (
                        <img src={getAvatarUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-primary/20 flex items-center justify-center text-sm text-primary font-bold">
                          {user.username?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white group-hover:text-primary transition-colors">{user.username}</p>
                      {isAdmin && <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] mt-0.5">Admin</p>}
                    </div>
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMenuOpen(false); }}
                  className="flex items-center gap-4 h-12 px-3 text-slate-300 hover:text-red-400 hover:bg-red-400/10 rounded-xl transition-all"
                >
                  <LogOut size={20} />
                  <span className="text-sm font-semibold">Abmelden</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ============ THEME QUICK PICKER MODAL ============ */}
      <AnimatePresence>
        {themeModalOpen && (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
            onClick={() => setThemeModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md p-6 rounded-2xl bg-[#0b0b0c] border border-zinc-900 space-y-5"
            >
              <div className="flex items-center justify-between pb-2 border-b border-zinc-900">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0">
                    <Palette size={16} className="text-amber-400" />
                  </span>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400/90">
                    Drivers Hub Farbe
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setThemeModalOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition-all cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Wähle deine individuelle Akzentfarbe für den DriversHub. Deine Auswahl wird direkt im Profil auf der Datenbank gespeichert.
              </p>

              {/* 8 Presets */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {presets.map((p) => {
                  const isSelected = (accentColor || '').toLowerCase() === p.hex.toLowerCase();
                  return (
                    <button
                      key={p.hex}
                      type="button"
                      onClick={() => {
                        setAccentColor(p.hex);
                        toast.success(`Akzentfarbe auf ${p.name} geändert`);
                      }}
                      className={`p-2.5 rounded-xl border text-left transition-all flex items-center gap-2 cursor-pointer ${
                        isSelected
                          ? 'bg-white/10 border-white/40'
                          : 'bg-black/40 border-white/5 hover:border-white/20 hover:bg-white/5'
                      }`}
                    >
                      <span
                        className="w-4 h-4 rounded-full shrink-0 shadow flex items-center justify-center border border-white/20"
                        style={{ backgroundColor: p.hex }}
                      >
                        {isSelected && <Check size={10} className="text-black font-bold" />}
                      </span>
                      <span className="text-[10px] font-bold text-white block truncate">{p.name}</span>
                    </button>
                  );
                })}
              </div>

              {/* Custom Picker & Hex */}
              <div className="p-3 rounded-xl bg-black/40 border border-white/5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <label className="relative cursor-pointer group">
                    <div
                      className="w-8 h-8 rounded-lg border border-white/10"
                      style={{ backgroundColor: accentColor }}
                    />
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      title="Eigene Farbe wählen"
                    />
                  </label>
                  <div>
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] block">Individuell</span>
                    <span className="text-xs text-white font-mono font-bold uppercase">{accentColor}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-black/80 rounded-lg border border-white/10 px-2 py-1">
                    <span className="text-[11px] text-zinc-500 font-mono mr-1">HEX</span>
                    <input
                      type="text"
                      value={customHex}
                      onChange={(e) => {
                        setCustomHex(e.target.value);
                        if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                          setAccentColor(e.target.value);
                        }
                      }}
                      placeholder="#f59e0b"
                      maxLength={7}
                      className="bg-transparent text-xs font-mono text-white outline-none w-18 uppercase"
                    />
                  </div>
                  {accentColor?.toLowerCase() !== defaultColor.toLowerCase() && (
                    <button
                      type="button"
                      onClick={() => {
                        resetAccentColor();
                        toast.success('Auf Standard (Amber Orange) zurückgesetzt');
                      }}
                      className="p-1.5 text-xs text-slate-400 hover:text-primary transition-colors cursor-pointer"
                      title="Auf Standard zurücksetzen"
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setThemeModalOpen(false)}
                className="w-full h-12 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold transition-colors flex items-center justify-center cursor-pointer"
              >
                Fertig
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ============ DOWNLOAD MODAL ============ */}
      <AnimatePresence>
        {downloadModalOpen && (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
            onClick={() => setDownloadModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md p-6 rounded-2xl bg-[#0b0b0c] border border-zinc-900 space-y-5"
            >
              <div className="flex items-center justify-between pb-2 border-b border-zinc-900">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0">
                    <Download size={16} className="text-amber-400" />
                  </span>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400/90">
                    Tracker herunterladen
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setDownloadModalOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Der OPC GameBridge Tracker überträgt Telemetrie, Traffic-Tracking und Route aus ETS2 direkt auf die Karte des Clubs.
              </p>

              <a
                href={SETUP_URL}
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold transition-colors"
              >
                <Download size={16} />
                Setup.exe herunterladen
              </a>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                <Monitor size={12} /> Windows · Direktdownload
              </p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
